import { PlacementType } from "@/types/inboxPlacements.types";
import { PaymentInterval } from "@/types/subscription.types";

export type PlanPrice = {
  [PaymentInterval.MONTHLY]: string;
  [PaymentInterval.QUARTERLY]: string;
  [PaymentInterval.SEMI_YEARLY]: string;
  [PaymentInterval.ANNUAL]: string;
};

export enum PlanCategory {
  B2B = "B2B",
  B2C = "B2C",
  SENDER_PROFILE = "SENDER_PROFILE",
  CUSTOM = "CUSTOM",
}

export enum B2BPlanType {
  STARTER = "Starter",
  GROWTH = "Growth",
  ENTERPRISE = "Enterprise",
  // CUSTOM = "Custom B2B",
}

export type JoinedPlanType = B2CPlanType | B2BPlanType | "CUSTOM";

export enum B2CPlanType {
  ESSENTIAL = "Essential",
  PRO = "Pro",
  BRAND = "Brand",
  // CUSTOM = "Custom B2C",
}

export type Plan = {
  name: string;
  type: B2CPlanType | B2BPlanType | "CUSTOM";
  category: PlanCategory;
  description: string;
  price: PlanPrice;
  earlyBirdPrice: PlanPrice;
  features: string[];
  limits: {
    warmupEmailsMonthly: number;
    warmupEmailsPerDay: number;
    // inboxPlacementTestsMonthly: number;
    // healthTestsMonthly: number;
    support: string;
    api: boolean;
  };
  cta: string;
  ctaVariant: "default" | "outline";
  popular?: boolean;
};

const b2cPlans: Plan[] = [
  {
    category: PlanCategory.B2C,
    name: "Essential",
    type: B2CPlanType.ESSENTIAL,
    description: "For beginners getting started with warmups.",
    price: {
      [PaymentInterval.MONTHLY]: "240",
      [PaymentInterval.QUARTERLY]: "216",
      [PaymentInterval.SEMI_YEARLY]: "204",
      [PaymentInterval.ANNUAL]: "192",
    },
    earlyBirdPrice: {
      [PaymentInterval.MONTHLY]: "120",
      [PaymentInterval.QUARTERLY]: "108",
      [PaymentInterval.SEMI_YEARLY]: "102",
      [PaymentInterval.ANNUAL]: "96",
    },
    features: [
      "500 Warmup Emails Send Per Day",
      "15,000 Warmup Emails Send Per Month",
      // "80 Email Health Test Per Month",
      // "40 Inbox Placement Test Per Month",
      "Add your Own Email Template",
      "Choose Your Warm Up Language",
      "Unlimited Google Postmaster Integration",
      "Unlimited Email Template Coach",
      "Email Support",
    ],

    limits: {
      warmupEmailsMonthly: 15000,
      warmupEmailsPerDay: 500,
      // inboxPlacementTestsMonthly: 40,
      // healthTestsMonthly: 80,
      support: "Email Support",
      api: false,
    },
    cta: "Buy Now",
    ctaVariant: "default" as const,
  },
  {
    category: PlanCategory.B2C,
    name: "Pro",
    type: B2CPlanType.PRO,
    description: "For growing businesses optimizing deliverability.",
    price: {
      [PaymentInterval.MONTHLY]: "480",
      [PaymentInterval.QUARTERLY]: "432",
      [PaymentInterval.SEMI_YEARLY]: "408",
      [PaymentInterval.ANNUAL]: "384",
    },
    earlyBirdPrice: {
      [PaymentInterval.MONTHLY]: "240",
      [PaymentInterval.QUARTERLY]: "216",
      [PaymentInterval.SEMI_YEARLY]: "204",
      [PaymentInterval.ANNUAL]: "192",
    },
    features: [
      "1,000 Warmup Emails Send Per Day",
      "30,000 Warmup Emails Send Per Month",
      // "160 Email Health Test Per Month",
      // "80 Inbox Placement Test Per Month",
      "Email Support",
      "Live Chat Support",
    ],

    limits: {
      warmupEmailsMonthly: 30000,
      warmupEmailsPerDay: 1000,
      // inboxPlacementTestsMonthly: 80,
      // healthTestsMonthly: 160,
      support: "Live Chat Support",
      api: false,
    },
    cta: "Buy Now",
    ctaVariant: "default" as const,
    popular: true,
  },
  {
    category: PlanCategory.B2C,
    name: "Brand",
    type: B2CPlanType.BRAND,
    description:
      "For brands managing large-scale campaigns and multiple domains.",
    price: {
      [PaymentInterval.MONTHLY]: "1440",
      [PaymentInterval.QUARTERLY]: "1298",
      [PaymentInterval.SEMI_YEARLY]: "1224",
      [PaymentInterval.ANNUAL]: "912",
    },
    earlyBirdPrice: {
      [PaymentInterval.MONTHLY]: "720",
      [PaymentInterval.QUARTERLY]: "648",
      [PaymentInterval.SEMI_YEARLY]: "612",
      [PaymentInterval.ANNUAL]: "456",
    },
    features: [
      "3,000 Warmup Emails Send Per Day",
      "90,000 Warmup Emails Send Per Month",
      // "480 Email Health Test Per Month",
      // "240 Inbox Placement Test Per Month",  
      "Priority Support",
      "Dedicated Account Manager",
      // "API Access",
    ],

    limits: {
      warmupEmailsMonthly: 90000,
      warmupEmailsPerDay: 3000,
      // inboxPlacementTestsMonthly: 240,
      // healthTestsMonthly: 480,
      support: "Dedicated Account Manager",
      api: false,
    },
    cta: "Buy Now",
    ctaVariant: "default" as const,
  },
];
const b2bPlans: Plan[] = [
  {
    category: PlanCategory.B2B,
    name: "Starter",
    type: B2BPlanType.STARTER,
    description: "For small teams starting out.",
    price: {
      [PaymentInterval.MONTHLY]: "4",
      [PaymentInterval.QUARTERLY]: "3.6",
      [PaymentInterval.SEMI_YEARLY]: "3.4",
      [PaymentInterval.ANNUAL]: "3.2",
    },
    earlyBirdPrice: {
      [PaymentInterval.MONTHLY]: "2",
      [PaymentInterval.QUARTERLY]: "1.8",
      [PaymentInterval.SEMI_YEARLY]: "1.7",
      [PaymentInterval.ANNUAL]: "1.6",
    },
    features: [
      "30 Warmup Emails Send Per Day",
      "900 Warmup Emails Send Per Month",
      "Add your Own Email Template",
      "Choose Your Warm Up Language",
      "Unlimited Google Postmaster Integration",
      "Unlimited Email Template Coach",
      "Email Support",
    ],
    limits: {
      warmupEmailsMonthly: 900,
      warmupEmailsPerDay: 30,
      // inboxPlacementTestsMonthly: 0,
      // healthTestsMonthly: 0,
      support: "Email Support",
      api: false,
    },
    cta: "Buy Now",
    ctaVariant: "default" as const,
  },
  {
    category: PlanCategory.B2B,
    name: "Growth",
    type: B2BPlanType.GROWTH,
    description: "For scaling organizations.",
    price: {
      [PaymentInterval.MONTHLY]: "8",
      [PaymentInterval.QUARTERLY]: "7.2",
      [PaymentInterval.SEMI_YEARLY]: "6.8",
      [PaymentInterval.ANNUAL]: "6.4",
    },
    earlyBirdPrice: {
      [PaymentInterval.MONTHLY]: "4",
      [PaymentInterval.QUARTERLY]: "3.6",
      [PaymentInterval.SEMI_YEARLY]: "3.4",
      [PaymentInterval.ANNUAL]: "3.2",
    },
    features: [
      "50 Warmup Emails Send Per Day",
      "1500 Warmup Emails Send Per Month",
      // "2 Email Health Test Per Month",
      // "1 Inbox Placement Test Per Month",
      "Email Support",
      "Live Chat Support",
    ],
    limits: {
      warmupEmailsMonthly: 1500,
      warmupEmailsPerDay: 50,
      // inboxPlacementTestsMonthly: 1,
      // healthTestsMonthly: 2,
      support: "Live Chat Support",
      api: false,
    },
    cta: "Buy Now",
    ctaVariant: "default" as const,
    popular: true,
  },
  {
    category: PlanCategory.B2B,
    name: "Enterprise",
    type: B2BPlanType.ENTERPRISE,
    description: "For high-volume, multi-domain ops.",
    price: {
      [PaymentInterval.MONTHLY]: "12",
      [PaymentInterval.QUARTERLY]: "10.8",
      [PaymentInterval.SEMI_YEARLY]: "10.2",
      [PaymentInterval.ANNUAL]: "9.6",
    },
    earlyBirdPrice: {
      [PaymentInterval.MONTHLY]: "6",
      [PaymentInterval.QUARTERLY]: "5.4",
      [PaymentInterval.SEMI_YEARLY]: "5.1",
      [PaymentInterval.ANNUAL]: "4.8",
    },
    features: [
      "100 Warmup Emails Send Per Day",
      "3000 Warmup Emails Send Per Month",
      // "4 Email Health Test Per Month",
      // "2 Inbox Placement Test Per Month",
      "Priority Support",
      "Dedicated Account Manager",
      // "API Access",
    ],

    limits: {
      warmupEmailsMonthly: 3000,
      warmupEmailsPerDay: 100,
      // inboxPlacementTestsMonthly: 2,
      // healthTestsMonthly: 4,
      support: "Dedicated Account Manager",
      api: false,
    },
    cta: "Buy Now",
    ctaVariant: "default" as const,
  },
];

export const customPlanData: Plan = {
  category: PlanCategory.CUSTOM,
  name: "Custom Plan",
  type: "CUSTOM",
  description:
    "For businesses with specialized requirements, we offer fully customized email warmup solutions. Work with our team to design a plan that aligns with your organization’s goals and scale.",
  price: {
    [PaymentInterval.MONTHLY]: "Custom",
    [PaymentInterval.QUARTERLY]: "Custom",
    [PaymentInterval.SEMI_YEARLY]: "Custom",
    [PaymentInterval.ANNUAL]: "Custom",
  },
  earlyBirdPrice: {
    [PaymentInterval.MONTHLY]: "Custom",
    [PaymentInterval.QUARTERLY]: "Custom",
    [PaymentInterval.SEMI_YEARLY]: "Custom",
    [PaymentInterval.ANNUAL]: "Custom",
  },
  features: [
    "Custom Email Warmup Limits",
    "Custom Mailbox/Domain Limits",
    "Custom Warmup Engagement Models",
    "Custom Dedicated Warmup Seed Pool ",
  ],
  limits: {
    warmupEmailsMonthly: 0,
    warmupEmailsPerDay: 0,
    // inboxPlacementTestsMonthly: 0,
    // healthTestsMonthly: 0,
    support: "Dedicated Account Manager",
    api: false,
  },
  cta: "Contact Sales",
  ctaVariant: "default" as const,
};

export const pricingData = {
  [PlacementType.B2C]: b2cPlans,
  [PlacementType.B2B]: b2bPlans,
};

export const b2bPlanObject = b2bPlans.reduce((acc, plan) => {
  acc[plan.type as B2BPlanType] = plan;
  return acc;
}, {} as Record<B2BPlanType, Plan>);

export const b2cPlanObject = b2cPlans.reduce((acc, plan) => {
  acc[plan.type as B2CPlanType] = plan;
  return acc;
}, {} as Record<B2CPlanType, Plan>);

// Order of plans in the plans page to show upgrade and downgrade options
export const b2cPlanOrder = [
  B2CPlanType.ESSENTIAL,
  B2CPlanType.PRO,
  B2CPlanType.BRAND,
];

export const b2bPlanOrder = [
  B2BPlanType.STARTER,
  B2BPlanType.GROWTH,
  B2BPlanType.ENTERPRISE,
];

export const intervalOrder = [
  PaymentInterval.MONTHLY,
  PaymentInterval.QUARTERLY,
  PaymentInterval.ANNUAL,
];
